import React, { useState, useEffect, useRef } from "react";
import { TextField, Select, Button } from "../ui";
import { FeatherX, FeatherPlus } from "@subframe/core";
import { BankGlAccountDropdown } from "../shared/presentation/components";
import { useDropdownCache } from "../shared/presentation/hooks";
import { GLMappingEntity } from "../shared/domain/entities/GLMappingEntity";

export interface TransferSplitItem {
  id: string;
  to_account_id: string;
  amount: number;
  payment_mode: string;
}

interface VendorTransferToOtherAccountsSplitRecoProps {
  transactionDate: string;
  transactionAmount: number;
  bankAccountId?: string;
  onDateChange: (date: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  onSplitsChange: (splits: TransferSplitItem[]) => void;
  zohoGl?: string;
}

const VendorTransferToOtherAccountsSplitReco: React.FC<
  VendorTransferToOtherAccountsSplitRecoProps
> = ({
  transactionDate,
  transactionAmount,
  bankAccountId,
  onDateChange,
  onReferenceNumberChange,
  onSplitsChange,
  zohoGl,
}) => {
  const [date, setDate] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [splits, setSplits] = useState<TransferSplitItem[]>([
    { id: "1", to_account_id: "", amount: 0, payment_mode: "Bank Transfer" },
    { id: "2", to_account_id: "", amount: 0, payment_mode: "Bank Transfer" },
  ]);

  const { getCached, setCached } = useDropdownCache();
  const accountNamesRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (transactionDate) {
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  useEffect(() => {
    onSplitsChange(splits);
  }, [splits, onSplitsChange]);

  const totalAmount = splits.reduce(
    (sum, split) => sum + (split.amount || 0),
    0,
  );
  const remainingAmount = transactionAmount - totalAmount;
  const isTotalValid = Math.abs(remainingAmount) < 0.01;

  const handleAddRow = () => {
    const newId = String(Date.now());
    setSplits([
      ...splits,
      {
        id: newId,
        to_account_id: "",
        amount: 0,
        payment_mode: "Bank Transfer",
      },
    ]);
  };

  const handleRemoveRow = (id: string) => {
    if (splits.length > 2) {
      setSplits(splits.filter((split) => split.id !== id));
    }
  };

  const updateSplit = (
    id: string,
    field: keyof TransferSplitItem,
    value: string | number,
  ) => {
    setSplits(
      splits.map((split) =>
        split.id === id ? { ...split, [field]: value } : split,
      ),
    );
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  const updateAccountName = (accountId: string, accountName: string) => {
    accountNamesRef.current.set(accountId, accountName);
  };

  const handleToAccountChange = (splitId: string, account: GLMappingEntity) => {
    const accountIdStr = String(account.account_id);
    updateAccountName(accountIdStr, account.gl_name);
    updateSplit(splitId, "to_account_id", accountIdStr);
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Date <span className="text-error-600">*</span>
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                type="date"
                value={date}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDateChange(event.target.value)
                }
              />
            </TextField>
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Reference #
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                placeholder="Enter reference number (optional)"
                value={referenceNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleReferenceNumberChange(event.target.value)
                }
              />
            </TextField>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col items-start gap-3">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-4 font-heading-4 text-default-font">
            Split Payments
          </span>
          <Button
            variant="neutral-secondary"
            size="small"
            onClick={handleAddRow}
          >
            <FeatherPlus className="mr-2 h-4 w-4" />
            Add Row
          </Button>
        </div>

        <div className="flex w-full flex-col gap-3">
          {splits.map((split) => (
            <div
              key={split.id}
              className="flex w-full items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4"
            >
              <div className="flex flex-1 items-center gap-3">
                <div className="flex flex-col items-start gap-2 flex-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    To Account <span className="text-error-600">*</span>
                  </span>
                  <BankGlAccountDropdown
                    preset="transfer_to_other"
                    bankAccountId={bankAccountId}
                    selectedAccountId={split.to_account_id}
                    selectedAccountName={
                      accountNamesRef.current.get(split.to_account_id) || ""
                    }
                    onSelect={(account) =>
                      handleToAccountChange(split.id, account)
                    }
                    placeholder="Select account"
                  />
                </div>
                <div className="flex flex-col items-start gap-2 flex-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Amount <span className="text-error-600">*</span>
                  </span>
                  <TextField className="w-full" label="" helpText="">
                    <TextField.Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={split.amount > 0 ? split.amount.toString() : ""}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateSplit(
                          split.id,
                          "amount",
                          parseFloat(event.target.value) || 0,
                        )
                      }
                      onWheel={(event: React.WheelEvent<HTMLInputElement>) => {
                        event.currentTarget.blur();
                        event.preventDefault();
                      }}
                    />
                  </TextField>
                </div>
                <div className="flex flex-col items-start gap-2 flex-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Transfer Via <span className="text-error-600">*</span>
                  </span>
                  <Select
                    label=""
                    helpText=""
                    value={split.payment_mode}
                    onValueChange={(value) =>
                      updateSplit(split.id, "payment_mode", value)
                    }
                  >
                    <Select.Item value="Bank Remittance">
                      Bank Remittance
                    </Select.Item>
                    <Select.Item value="Bank Transfer">
                      Bank Transfer
                    </Select.Item>
                    <Select.Item value="Cash">Cash</Select.Item>
                    <Select.Item value="Cheque">Cheque</Select.Item>
                    <Select.Item value="Credit Card">Credit Card</Select.Item>
                    <Select.Item value="UPI">UPI</Select.Item>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={() => handleRemoveRow(split.id)}
                    disabled={splits.length <= 2}
                  >
                    <FeatherX className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex w-full flex-col gap-2 rounded-md border border-solid border-neutral-border bg-white p-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Transaction Amount:
            </span>
            <span className="text-body-bold font-body-bold text-default-font">
              ₹
              {transactionAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Total Split Amount:
            </span>
            <span className="text-body-bold font-body-bold text-default-font">
              ₹
              {totalAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Remaining:
            </span>
            <span
              className={`text-body-bold font-body-bold ${
                isTotalValid
                  ? "text-success-600"
                  : remainingAmount > 0
                    ? "text-warning-600"
                    : "text-error-600"
              }`}
            >
              ₹
              {remainingAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          {!isTotalValid && (
            <div className="mt-2 text-caption text-error-600">
              {remainingAmount > 0
                ? `Please add ₹${remainingAmount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} more to match the transaction amount.`
                : `Total split amount exceeds transaction amount by ₹${Math.abs(
                    remainingAmount,
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}.`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorTransferToOtherAccountsSplitReco;
